import React, { useState } from 'react';
import './YoutubeStoryteller.css';

const YoutubeStorytellerStep2 = ({ formData, onBack, onComplete }) => {
  const { newsArticles = [], storyBlocks = [], narrativeStories = [], usage = {}, language = 'german' } = formData;
  const [selectedNews, setSelectedNews] = useState([]);
  const [selectedBlocks, setSelectedBlocks] = useState([]);
  const [selectedNarratives, setSelectedNarratives] = useState([]);
  const [activeTab, setActiveTab] = useState('all'); // 'all', 'news', 'blocks', or 'narratives'
  const [expandedCards, setExpandedCards] = useState([]); // Track expanded cards
  const [notes, setNotes] = useState(''); // Notes field
  const [isNotesOpen, setIsNotesOpen] = useState(true); // Notes panel visibility
  
  // Language-specific texts
  const isGerman = language === 'german';
  const texts = {
    tabs: {
      allItems: isGerman ? 'Alle Items' : 'All Items',
      newsTrends: isGerman ? 'News & Trends' : 'News & Trends',
      storyAngles: isGerman ? 'Story Angles' : 'Story Angles',
      moreIdeas: isGerman ? 'Weitere Ideen' : 'More Ideas'
    },
    headers: {
      newsTitle: isGerman ? '📰 Aktuelle News & Trends (Letzte 48 Stunden)' : '📰 Current News & Trends (Last 48 Hours)',
      newsSubtitle: isGerman ? 'Echte Artikel mit Quellen-Links - verbinde dein Thema mit dem, was JETZT passiert' : 'Real articles with source links - connect your topic to what\'s happening NOW',
      blocksTitle: isGerman ? '🎬 Story Angles' : '🎬 Story Angles',
      blocksSubtitle: isGerman ? 'Konkrete Story-Themen und Perspektiven, zugeschnitten auf dein Thema' : 'Concrete story themes and perspectives tailored to your topic',
      narrativesTitle: isGerman ? '📖 Weitere Ideen - Vollständige Video-Story-Konzepte' : '📖 More Ideas - Complete Video Story Concepts',
      narrativesSubtitle: isGerman ? 'Praktische, umsetzbare Story-Ideen mit vollständiger Narrative und Filming-Tipps' : 'Practical, actionable story ideas with full narrative and filming tips'
    },
    buttons: {
      back: isGerman ? 'Zurück' : 'Back',
      continue: isGerman ? 'Weiter mit' : 'Continue with',
      item: isGerman ? 'Item' : 'Item',
      items: isGerman ? 'Items' : 'Items',
      showMore: isGerman ? 'Mehr anzeigen' : 'Show More',
      showLess: isGerman ? 'Weniger anzeigen' : 'Show Less'
    },
    notes: {
      title: isGerman ? 'Notizen' : 'Notes',
      placeholder: isGerman ? 'Schreibe deine Notizen hier... Diese werden automatisch zu deinen Custom Instructions im nächsten Schritt hinzugefügt.' : 'Write your notes here... These will be automatically added to your custom instructions in the next step.',
      hint: isGerman ? 'Füllt Step 3 automatisch aus' : 'Auto-fills Step 3',
      charCount: isGerman ? 'Zeichen' : 'characters',
      minimize: isGerman ? 'Minimieren' : 'Minimize'
    },
    labels: {
      yourAngle: isGerman ? 'Dein Angle' : 'Your Angle',
      readSource: isGerman ? 'Quelle lesen' : 'Read Source'
    }
  };

  const toggleCardExpansion = (cardId) => {
    setExpandedCards(prev => 
      prev.includes(cardId) 
        ? prev.filter(id => id !== cardId)
        : [...prev, cardId]
    );
  };

  const toggleNews = (newsId) => {
    setSelectedNews(prev => {
      if (prev.includes(newsId)) {
        return prev.filter(id => id !== newsId);
      } else {
        return [...prev, newsId];
      }
    });
  };

  const toggleBlock = (blockId) => {
    setSelectedBlocks(prev => {
      if (prev.includes(blockId)) {
        return prev.filter(id => id !== blockId);
      } else {
        return [...prev, blockId];
      }
    });
  };

  const toggleNarrative = (narrativeId) => {
    setSelectedNarratives(prev => {
      if (prev.includes(narrativeId)) {
        return prev.filter(id => id !== narrativeId);
      } else {
        return [...prev, narrativeId];
      }
    });
  };

  const handleContinue = () => {
    const totalSelected = selectedNews.length + selectedBlocks.length + selectedNarratives.length;
    
    if (totalSelected === 0) {
      alert('Please select at least one item to continue');
      return;
    }

    const selectedNewsObjects = newsArticles.filter(news => 
      selectedNews.includes(news.id)
    );
    const selectedBlockObjects = storyBlocks.filter(block => 
      selectedBlocks.includes(block.id)
    );
    const selectedNarrativeObjects = narrativeStories.filter(narrative => 
      selectedNarratives.includes(narrative.id)
    );

    onComplete({
      ...formData,
      selectedNews: selectedNewsObjects,
      selectedBlocks: selectedBlockObjects,
      selectedNarratives: selectedNarrativeObjects,
      blockNotes: notes // Pass notes to next step
    });
  };

  const getViralBadgeClass = (potential) => {
    return potential === 'high' ? 'yt-viral-high' : 'yt-viral-medium';
  };

  const getCategoryIcon = (category) => {
    const icons = {
      'tech': '💻',
      'culture': '🎭',
      'business': '💼',
      'social-media': '📱',
      'viral': '🔥',
      'breaking': '⚡',
      'hook': '🎣',
      'story-structure': '📖',
      'content-strategy': '💡',
      'emotional-arc': '🎭',
      'execution-tactic': '🎬',
      'cta': '📣',
      'personal-narrative': '📖',
      'metaphorical-tale': '🎭',
      'scene-based': '🌅',
      'internal-monologue': '💭',
      'dialogue-driven': '🗣️',
      'action-sequence': '🏃'
    };
    return icons[category] || '📌';
  };

  const getViralityLevel = (score) => {
    const numScore = Number(score) || 0;
    if (numScore >= 8.0) return 'high';
    if (numScore >= 5.0) return 'medium';
    return 'low';
  };

  const formatViralityScore = (score) => {
    const numScore = Number(score) || 0;
    return numScore.toFixed(1);
  };

  const totalSelected = selectedNews.length + selectedBlocks.length + selectedNarratives.length;

  // Sort all items by virality score for "All Items" tab
  const allItemsSorted = [
    ...newsArticles.map(item => ({ ...item, type: 'news' })),
    ...storyBlocks.map(item => ({ ...item, type: 'block' })),
    ...narrativeStories.map(item => ({ ...item, type: 'narrative' }))
  ].sort((a, b) => (b.viralityScore || 0) - (a.viralityScore || 0));

  return (
    <div className="yt-step2-container">
      {/* Header */}
     
      {/* Tab Navigation */}
      <div className="yt-tab-navigation">
        <button
          className={`yt-tab-btn ${activeTab === 'all' ? 'active' : ''}`}
          onClick={() => setActiveTab('all')}
        >
          <span className="yt-tab-icon"><i className="fas fa-star"></i></span>
          <span className="yt-tab-label">
            {texts.tabs.allItems}
            {totalSelected > 0 && (
              <span className="yt-tab-count">{totalSelected}</span>
            )}
          </span>
        </button>
        <button
          className={`yt-tab-btn ${activeTab === 'news' ? 'active' : ''}`}
          onClick={() => setActiveTab('news')}
        >
          <span className="yt-tab-icon"><i className="fas fa-newspaper"></i></span>
          <span className="yt-tab-label">
            {texts.tabs.newsTrends}
            {selectedNews.length > 0 && (
              <span className="yt-tab-count">{selectedNews.length}</span>
            )}
          </span>
        </button>
        <button
          className={`yt-tab-btn ${activeTab === 'blocks' ? 'active' : ''}`}
          onClick={() => setActiveTab('blocks')}
        >
          <span className="yt-tab-icon"><i className="fas fa-film"></i></span>
          <span className="yt-tab-label">
            {texts.tabs.storyAngles}
            {selectedBlocks.length > 0 && (
              <span className="yt-tab-count">{selectedBlocks.length}</span>
            )}
          </span>
        </button>
        <button
          className={`yt-tab-btn ${activeTab === 'narratives' ? 'active' : ''}`}
          onClick={() => setActiveTab('narratives')}
        >
          <span className="yt-tab-icon"><i className="fas fa-book"></i></span>
          <span className="yt-tab-label">
            {texts.tabs.moreIdeas}
            {selectedNarratives.length > 0 && (
              <span className="yt-tab-count">{selectedNarratives.length}</span>
            )}
          </span>
        </button>
      </div>

      {/* ALL TAB */}
      {activeTab === 'all' && (
        <div className="yt-tab-content">
      
          
          <div className="yt-cards-grid">
            {/* Render All Items Sorted by Virality Score */}
            {allItemsSorted.map((item, index) => {
              const isNews = item.type === 'news';
              const isBlock = item.type === 'block';
              const isNarrative = item.type === 'narrative';
              
              const isSelected = isNews 
                ? selectedNews.includes(item.id)
                : isBlock 
                  ? selectedBlocks.includes(item.id)
                  : selectedNarratives.includes(item.id);
              
              const toggleItem = isNews
                ? () => toggleNews(item.id)
                : isBlock
                  ? () => toggleBlock(item.id)
                  : () => toggleNarrative(item.id);

              const cardClass = isNews 
                ? 'yt-news-card'
                : isBlock
                  ? 'yt-block-card'
                  : 'yt-narrative-card';
              
              const cardId = `${item.type}-${item.id}`;
              const isExpanded = expandedCards.includes(cardId);
              const isTopRated = index < 3;
              
              return (
                <div
                  key={cardId}
                  className={`yt-card ${cardClass} ${isSelected ? 'selected' : ''} ${isExpanded ? 'expanded' : ''}`}
                  data-virality={getViralityLevel(item.viralityScore)}
                  onClick={toggleItem}
                >
                  <div className="yt-card-select-badge">
                    {isSelected ? (
                      <i className="fas fa-check-circle"></i>
                    ) : (
                      <i className="far fa-circle"></i>
                    )}
                  </div>

                  {item.viralityScore !== undefined && (
                    <div className="yt-virality-score">
                      {formatViralityScore(item.viralityScore)}
                    </div>
                  )}

                  <h3 className="yt-card-title">{item.title}</h3>

                  {item.subtitle && (
                    <p className="yt-card-subtitle">{item.subtitle}</p>
                  )}

                  {item.theme && (
                    <p className="yt-card-subtitle">🎯 {item.theme}</p>
                  )}

                  {/* Story Block Description - Structured */}
                  {isBlock && item.description && (
                    <div className="yt-block-story-content">
                      <div className="yt-block-description">
                        {item.description.split('. ').map((sentence, idx, arr) => {
                          const text = idx < arr.length - 1 && !sentence.endsWith('.') ? sentence + '.' : sentence;
                          return text.trim() ? <p key={idx}>{text}</p> : null;
                        })}
                      </div>
                    </div>
                  )}

                  {/* Narrative Description - Structured */}
                  {isNarrative && item.description && (
                    <div className="yt-narrative-story-content">
                      <div className="yt-story-text">
                        {item.description.split('. ').map((sentence, idx, arr) => {
                          const text = idx < arr.length - 1 && !sentence.endsWith('.') ? sentence + '.' : sentence;
                          return text.trim() ? <p key={idx}>{text}</p> : null;
                        })}
                      </div>
                    </div>
                  )}

                  {/* News Description - Simple */}
                  {isNews && item.description && (
                    <p className="yt-card-description">{item.description}</p>
                  )}

                  {item.sceneDescription && (
                    <div className="yt-scene-description">
                      <strong>🎬 Scene:</strong>
                      <p>{item.sceneDescription}</p>
                    </div>
                  )}

                  {/* News-specific: Source Link */}
                  {isNews && item.sourceUrl && (
                    <a 
                      href={item.sourceUrl} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="yt-source-link"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <i className="fas fa-external-link-alt"></i>
                      <span>{item.sourceName || 'Read Source'}</span>
                      {item.publishedDate && (
                        <span className="yt-source-date"> · {item.publishedDate}</span>
                      )}
                    </a>
                  )}

                  {/* News-specific: Relevance */}
                  {isNews && item.relevanceToTopic && (
                    <div className="yt-relevance">
                      <i className="fas fa-lightbulb"></i>
                      <span><strong>{texts.labels.yourAngle}:</strong> {item.relevanceToTopic}</span>
                    </div>
                  )}

                  {/* News-specific: Engagement */}
                  {isNews && item.viewsOrEngagement && (
                    <div className="yt-engagement">
                      <i className="fas fa-fire"></i>
                      <span>{item.viewsOrEngagement}</span>
                    </div>
                  )}

                  {/* Expand/Collapse Button */}
                  <button
                    className="yt-card-expand-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleCardExpansion(cardId);
                    }}
                  >
                    <span className="yt-card-expand-btn-inner">
                      {isExpanded ? texts.buttons.showLess : texts.buttons.showMore}
                      <i className="fas fa-chevron-down"></i>
                    </span>
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* NEWS TAB */}
      {activeTab === 'news' && (
        <div className="yt-tab-content">
          <div className="yt-tab-header">
            <h3 className="yt-tab-title">{texts.headers.newsTitle}</h3>
            <p className="yt-tab-subtitle">{texts.headers.newsSubtitle}</p>
          </div>
          
          <div className="yt-cards-grid">
            {newsArticles.map((news) => {
              const isSelected = selectedNews.includes(news.id);
              
              return (
                <div
                  key={news.id}
                  className={`yt-card yt-news-card ${isSelected ? 'selected' : ''}`}
                  data-virality={getViralityLevel(news.viralityScore)}
                  onClick={() => toggleNews(news.id)}
                >
                  {/* Selection Indicator */}
                  <div className="yt-card-select-badge">
                    {isSelected ? (
                      <i className="fas fa-check-circle"></i>
                    ) : (
                      <i className="far fa-circle"></i>
                    )}
                  </div>

                  {/* Virality Score */}
                  {news.viralityScore !== undefined && (
                    <div className="yt-virality-score">
                      {formatViralityScore(news.viralityScore)}
                    </div>
                  )}

                  {/* Title */}
                  <h3 className="yt-card-title">{news.title}</h3>

                  {/* Subtitle */}
                  {news.subtitle && (
                    <p className="yt-card-subtitle">{news.subtitle}</p>
                  )}

                  {/* Description */}
                  <p className="yt-card-description">{news.description}</p>

                  {/* Source Link */}
                  {news.sourceUrl && (
                    <a 
                      href={news.sourceUrl} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="yt-source-link"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <i className="fas fa-external-link-alt"></i>
                      <span>{news.sourceName || texts.labels.readSource}</span>
                      {news.publishedDate && (
                        <span className="yt-source-date"> · {news.publishedDate}</span>
                      )}
                    </a>
                  )}

                  {/* Relevance to Topic */}
                  {news.relevanceToTopic && (
                    <div className="yt-relevance">
                      <i className="fas fa-lightbulb"></i>
                      <span><strong>{texts.labels.yourAngle}:</strong> {news.relevanceToTopic}</span>
                    </div>
                  )}

                  {/* Engagement Stats */}
                  {news.viewsOrEngagement && (
                    <div className="yt-engagement">
                      <i className="fas fa-fire"></i>
                      <span>{news.viewsOrEngagement}</span>
                    </div>
                  )}

                  {/* Tags */}
                  {news.tags && news.tags.length > 0 && (
                    <div className="yt-card-tags">
                      {news.tags.map((tag, idx) => (
                        <span key={idx} className="yt-card-tag">
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* STORY BLOCKS TAB */}
      {activeTab === 'blocks' && (
        <div className="yt-tab-content">
          <div className="yt-tab-header">
            <h3 className="yt-tab-title">{texts.headers.blocksTitle}</h3>
            <p className="yt-tab-subtitle">{texts.headers.blocksSubtitle}</p>
          </div>
          
          <div className="yt-cards-grid">
            {storyBlocks.map((block) => {
              const isSelected = selectedBlocks.includes(block.id);
              
              return (
                <div
                  key={block.id}
                  className={`yt-card yt-block-card ${isSelected ? 'selected' : ''}`}
                  data-virality={getViralityLevel(block.viralityScore)}
                  onClick={() => toggleBlock(block.id)}
                >
                  {/* Selection Indicator */}
                  <div className="yt-card-select-badge">
                    {isSelected ? (
                      <i className="fas fa-check-circle"></i>
                    ) : (
                      <i className="far fa-circle"></i>
                    )}
                  </div>

                  {/* Virality Score */}
                  {block.viralityScore !== undefined && (
                    <div className="yt-virality-score">
                      {formatViralityScore(block.viralityScore)}
                    </div>
                  )}

                  {/* Title */}
                  <h3 className="yt-card-title">{block.title}</h3>

                  {/* Subtitle */}
                  {block.subtitle && (
                    <p className="yt-card-subtitle">{block.subtitle}</p>
                  )}

                  {/* Description */}
                  {block.description && (
                    <div className="yt-block-story-content">
                      <div className="yt-block-description">
                        {block.description.split('. ').map((sentence, idx, arr) => {
                          // Add period back except for last sentence
                          const text = idx < arr.length - 1 && !sentence.endsWith('.') ? sentence + '.' : sentence;
                          return text.trim() ? <p key={idx}>{text}</p> : null;
                        })}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* MORE IDEAS TAB (Narrative Stories) */}
      {activeTab === 'narratives' && (
        <div className="yt-tab-content">
          <div className="yt-tab-header">
            <h3 className="yt-tab-title">{texts.headers.narrativesTitle}</h3>
            <p className="yt-tab-subtitle">{texts.headers.narrativesSubtitle}</p>
          </div>
          
          <div className="yt-cards-grid">
            {narrativeStories.map((narrative) => {
              const isSelected = selectedNarratives.includes(narrative.id);
              
              return (
                <div
                  key={narrative.id}
                  className={`yt-card yt-narrative-card ${isSelected ? 'selected' : ''}`}
                  data-virality={getViralityLevel(narrative.viralityScore)}
                  onClick={() => toggleNarrative(narrative.id)}
                >
                  <div className="yt-card-select-badge">
                    {isSelected ? (
                      <i className="fas fa-check-circle"></i>
                    ) : (
                      <i className="far fa-circle"></i>
                    )}
                  </div>

                  {narrative.viralityScore !== undefined && (
                    <div className="yt-virality-score">
                      {formatViralityScore(narrative.viralityScore)}
                    </div>
                  )}

                  <div className="yt-narrative-header">
                    <h3 className="yt-card-title">{narrative.title}</h3>
                    
                    {narrative.subtitle && (
                      <p className="yt-card-subtitle">🎯 {narrative.subtitle}</p>
                    )}

                    {narrative.storyType && (
                      <div className="yt-category-narrative">
                        📖 {narrative.storyType.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
                      </div>
                    )}
                  </div>

                  {narrative.description && (
                    <div className="yt-narrative-story-content">
                      <div className="yt-story-text">
                        {narrative.description.split('. ').map((sentence, idx, arr) => {
                          // Add period back except for last sentence
                          const text = idx < arr.length - 1 && !sentence.endsWith('.') ? sentence + '.' : sentence;
                          return text.trim() ? <p key={idx}>{text}</p> : null;
                        })}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="yt-form-actions">
        <button 
          type="button" 
          className="yt-btn-secondary" 
          onClick={onBack}
        >
          <i className="fas fa-arrow-left"></i>
          {texts.buttons.back}
        </button>
        
        <button 
          type="button" 
          className="yt-btn-primary" 
          onClick={handleContinue}
          disabled={totalSelected === 0}
        >
          <i className="fas fa-arrow-right"></i>
          {texts.buttons.continue} {totalSelected} {totalSelected !== 1 ? texts.buttons.items : texts.buttons.item} ({selectedNews.length} News + {selectedBlocks.length} Angles + {selectedNarratives.length} Stories)
        </button>
      </div>

      {/* Fixed Continue Button */}
      {totalSelected > 0 && (
        <button
          className="yt-fixed-continue-btn"
          onClick={handleContinue}
          title={`${texts.buttons.continue} ${totalSelected} ${totalSelected !== 1 ? texts.buttons.items : texts.buttons.item}`}
        >
          <i className="fas fa-arrow-right"></i>
          <span className="yt-fixed-continue-count">{totalSelected}</span>
        </button>
      )}

      {/* Fixed Notes Panel */}
      <div className={`yt-fixed-notes-panel ${isNotesOpen ? 'open' : 'closed'} ${totalSelected > 0 ? 'with-continue-btn' : ''}`}>
        {/* Toggle Button */}
        {!isNotesOpen && (
          <button 
            className="yt-notes-toggle-btn"
            onClick={() => setIsNotesOpen(true)}
            title={isGerman ? 'Notizen öffnen' : 'Open Notes'}
          >
            <i className="fas fa-sticky-note"></i>
            <span className="yt-notes-toggle-label">{texts.notes.title}</span>
            {notes.length > 0 && (
              <span className="yt-notes-badge">{notes.length}</span>
            )}
          </button>
        )}
        
        {/* Panel Content */}
        {isNotesOpen && (
          <>
            <div className="yt-notes-header">
              <div className="yt-notes-header-left">
                <i className="fas fa-sticky-note"></i>
                <h3 className="yt-notes-title">{texts.notes.title}</h3>
              </div>
              <button 
                className="yt-notes-close-btn"
                onClick={() => setIsNotesOpen(false)}
                title={texts.notes.minimize}
              >
                <i className="fas fa-chevron-down"></i>
              </button>
            </div>
            <textarea
              className="yt-notes-textarea"
              placeholder={texts.notes.placeholder}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={15}
            />
            <div className="yt-notes-footer">
              <span className="yt-notes-hint">
                <i className="fas fa-info-circle"></i>
                {texts.notes.hint}
              </span>
              <span className="yt-notes-char-count">
                {notes.length} {texts.notes.charCount}
              </span>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default YoutubeStorytellerStep2;

